import { useDomain } from '@app/hooks/useDomain';
import { useLazyGetCurrenciesQuery } from '@entities/currency';
import { useLazyGetMerchantsQuery } from '@entities/merchant';
import { useMethods } from '@entities/method';
import {
  TRequest,
  TRequestType,
  TRequestTableColumns,
  useRejectRequestMutation,
  useRefundRequestMutation,
} from '@entities/requests';
import { IRequestDepositTableColumns } from '@entities/requests/deposit';
import { IRequestWithdrawalTableColumns } from '@entities/requests/withdrawal';
import { trafficTypes } from '@entities/traffic';
import { TUserRoles, useUserRole } from '@entities/user';
import EmojiIcon from '@features/emoji-icon';
import HighlightIcon from '@features/highlight-icon';
import ReduceAndCopyString from '@features/reduce-and-copy-string';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';
import TableCheckAllRows from '@features/table-check-all-rows';
import TableCheckOneRow from '@features/table-check-one-row';
import {
  FilterControlBlock,
  FilterDateRange,
  FilterInput,
  FilterSelect,
  FilterSelectSync,
} from '@features/table-filter';
import { ViewSlip } from '@features/view-slip';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getSelectOptions } from '@shared/components/Form';
import RequestStatusLabel from '@shared/components/RequestStatusLabel';
import { formatAmount } from '@shared/utils/formatAmount';
import formatSecondsToTime from '@shared/utils/formatSecondsToTime';
import { pickKeysFromObject } from '@shared/utils/pickKeysFromObject';
import { removeKeysFromObject } from '@shared/utils/removeKeysFromObject';
import { ApproveOrderKRW } from '@widgets/orders';

import { getLazyFilterQuery } from './getLazyFilterQuery';
import { ApproveRequest } from '../components/ApproveRequest';
import { BulkRequests } from '../components/BulkRequests';
import { ConfirmApproveRefundRequest } from '../components/ConfirmApproveRefundRequest';
import { ConfirmApproveRejectRequest } from '../components/ConfirmApproveRejectRequest';
import { ViewRequest } from '../components/ViewRequest';

const restrictedProviderDomainHashes = [
  'b97bb222bd36249474d6e298a358568c067aa6aaa31f2900d969cfbfc11a43b8', // stage.lakhpay.com
  'f3a844899bc64ed78a238c842ed9c9eec7fabd8ad6be8b6fe887649db744b3b9', // prod.lakhpay.com
];

const restrictedProviderAllowedColumns = [
  'order_id', 'account', 'created_at', 'updated_at', 'processing_time',
  'requested_amount', 'actual_amount', 'currency', 'customer_transaction_id',
  'status', 'amount_mismatch', 'recipient_account_name', 'recipient_account_number', 'recipient_bank_name',
] as const;

interface IProps {
  type: TRequestType;
  refetch?: () => void;
  setRefreshInterval?: (interval: string) => void;
  refreshInterval?: string;
}

export const useTableSettings = ({
  type,
  refetch,
  setRefreshInterval,
  refreshInterval,
}: IProps) => {
  const userRole = useUserRole();
  const hashedDomain = useDomain();

  const isRestrictedProvider =
    userRole === 'provider' &&
    restrictedProviderDomainHashes.includes(hashedDomain ?? '');

  const [getRequestFilterOptions] = getLazyFilterQuery(type)();
  const [getMerchantFilterOptions] = useLazyGetMerchantsQuery();
  const [getCurrencyFilterOptions] = useLazyGetCurrenciesQuery();

  // THead settings
  const depositColumnTitles: IRequestDepositTableColumns = {
    id: 'Request ID',
    order_id: 'Order ID',
    method: 'Method',
    account: 'Account',
    merchant_short_name: 'Merchant Short Name',
    merchant_order_id: 'Merchant order ID',
    created_at: 'Request Date',
    updated_at: 'Update Date',
    processing_time: 'Processing Time',
    traffic: 'Traffic',
    merchant_customer_id: 'Merchant User ID',
    requested_amount: 'Requested Amount',
    actual_amount: 'Actual Amount',
    discount_amount: 'Discount Amount',
    currency: 'Currency',
    merchant_wallet: 'Merchant Wallet',
    customer_transaction_id: 'UTR',
    status: 'Status',
    amount_mismatch: 'Amount Mismatch',
    status_reason: 'Status Reason',

    recipient_account_name: 'Recipient Account Name',
    recipient_account_number: 'Recipient Account Number',
    recipient_bank_name: 'Recipient Bank Name',
    sender_account_name: 'Sender Account Name',
    sender_account_number: 'Sender Account Number',
    sender_bank_name: 'Sender Bank Name',
    slip: 'Slip',
  };

  const withdrawalColumnTitles: IRequestWithdrawalTableColumns = {
    id: 'Request ID',
    order_id: 'Order ID',
    method: 'Method',
    account: 'Account',
    merchant_short_name: 'Merchant Short Name',
    customer_bank_account: 'Bank account',
    customer_bank_code: 'IFSC code',
    customer_account_name: 'Beneficiary',
    created_at: 'Request Date',
    updated_at: 'Updated Date',
    processing_time: 'Processing Time',
    phone: 'Phone',
    traffic: 'Traffic',
    merchant_customer_id: 'Merchant User ID',
    requested_amount: 'Requested Amount',
    actual_amount: 'Actual Amount',
    discount_amount: 'Discount Amount',
    currency: 'Currency',
    customer_transaction_id: 'UTR',
    human_hash: 'Human Hash',
    status: 'Status',
    amount_mismatch: 'Amount Mismatch',
    status_reason: 'Status Reason',

    recipient_account_name: 'Recipient Account Name',
    recipient_account_number: 'Recipient Account Number',
    recipient_bank_name: 'Recipient Bank Name',
    sender_account_name: 'Sender Account Name',
    sender_account_number: 'Sender Account Number',
    sender_bank_name: 'Sender Bank Name',
    slip: 'Slip',
  };

  const hiddenColumnKeys: (keyof TRequestTableColumns)[] = [
    'id',
    'order_id',
    'method',
    'merchant_order_id',
    'account',
    'traffic',
  ];

  type TColumnTitles = {
    [role in TRequestType]: {
      [type in TUserRoles]:
      | Partial<IRequestDepositTableColumns>
      | Partial<IRequestWithdrawalTableColumns>;
    };
  };
  const getColumnTitles = (type: TRequestType, userRole: TUserRoles) => {
    if (!type || !userRole) return {} as TRequestTableColumns;

    if (isRestrictedProvider) {
      const columnTitles = type === 'deposit' ? depositColumnTitles : withdrawalColumnTitles;
      return pickKeysFromObject(columnTitles, [...restrictedProviderAllowedColumns]) as TRequestTableColumns;
    }

    // providers never see the discount amount; the restricted lakhpay allow-list above excludes it already
    const columnTitles: TColumnTitles = {
      deposit: {
        admin: depositColumnTitles,
        merchant: depositColumnTitles,
        operator: depositColumnTitles,
        provider: removeKeysFromObject(depositColumnTitles, ['traffic', 'discount_amount']),
        user: depositColumnTitles,
      },
      withdrawal: {
        admin: withdrawalColumnTitles,
        merchant: withdrawalColumnTitles,
        operator: withdrawalColumnTitles,
        provider: removeKeysFromObject(withdrawalColumnTitles, ['traffic', 'discount_amount']),
        user: withdrawalColumnTitles,
      },
    };

    return columnTitles[type][userRole] as TRequestTableColumns;
  };

  const filterStatuses = [
    'pending',
    'new',
    'approved',
    'rejected',
    'mismatch',
    'invalid',
    'expired',
    'refunded',
  ];

  const filterAmountMismatch = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' },
    { id: 'rooster', label: 'Rooster 🐓' },
  ];

  const { methods } = useMethods({
    disableUrlFilter: true,
  });

  const allowedStatusesSingleAction = ['pending', 'new', 'mismatch', 'invalid', 'expired'];
  const allowedStatusesBulkAction = ['pending', 'new', 'mismatch', 'invalid', 'expired'];
  const renderFilterElement = (key: keyof TRequest) => {
    switch (key) {
      case 'method':
        return (
          <FilterSelect
            name={key}
            options={methods?.data.map(method => ({ id: method.name, label: method.name })) ?? []}
          />
        );

      case 'currency':
        return (
          <FilterSelectSync
            getFilterOptions={getCurrencyFilterOptions}
            relationFilterKey='currency'
            name='currency'
            labelKey='code'
            valueKey='code'
            filterTestModeKey='is_test'
          />
        );

      case 'merchant_short_name':
        return (
          <FilterSelectSync
            getFilterOptions={getMerchantFilterOptions}
            relationFilterKey='merchant_short_name'
            name='merchant_short_name'
            labelKey='short_name'
            valueKey='short_name'
            filterTestModeKey='is_test'
          />
        );

      case 'account':
        return (
          <FilterSelectSync
            getFilterOptions={getRequestFilterOptions}
            relationFilterKey='account_name'
            name='account'
            labelKey='account'
            valueKey='account'
          />
        );

      case 'status':
        return <FilterSelect name={key} options={getSelectOptions(filterStatuses)} />;

      case 'amount_mismatch':
        return <FilterSelect name={key} options={filterAmountMismatch} />;

      case 'traffic':
        return <FilterSelect name={key} options={getSelectOptions(trafficTypes)} />;

      case 'created_at':
      case 'updated_at':
        return <FilterDateRange name={key} />;

      case 'slip':
        return null;

      default:
        return <FilterInput name={key} />;
    }
  };

  const getTheadBefore = (requests: TRequest[]) => {
    if (userRole !== 'admin' && userRole !== 'operator') return null;
    return (
      <th className='hidden-on-mob'>
        <TableCheckAllRows items={requests} filterKeys={allowedStatusesBulkAction} />
      </th>
    );
  };

  const getTheadAfter = (requests: TRequest[]) => (
    <th>
      <FilterControlBlock storageKey={`request-${type}`}>
        <BulkRequests
          refreshInterval={refreshInterval}
          setRefreshInterval={setRefreshInterval}
          refetch={refetch}
        />
      </FilterControlBlock>
    </th>
  );

  // TBody settings
  const renderRow = (key: keyof TRequestTableColumns, request: TRequest) => {
    switch (key) {
      case 'id':
      case 'order_id':
      case 'merchant_order_id':
      case 'merchant_customer_id':
        return <ReduceAndCopyString text={request[key]} />;

      case 'customer_transaction_id':
        return (
          <ReduceAndCopyString
            allowedNavigateRoles={['admin', 'operator']}
            navigateTo={`/transactions/${type}?filter[utr]=${request[key]}`}
            text={request[key]}
            needReduce={false}
            size={6}
          />
        );

      case 'processing_time':
        return formatSecondsToTime(request[key]);

      case 'discount_amount':
      case 'actual_amount':
      case 'requested_amount':
        return request[key] && `${formatAmount(String(request[key]))}`;

      case 'status':
        return <RequestStatusLabel status={String(request[key])} />;

      case 'amount_mismatch':
        return (
          <>
            {request[key] && (
              <HighlightIcon
                text={
                  request.critical_amount_mismatch ? 'Critical amount mismatch!' : 'Amount mismatch'
                }
              >
                {request.critical_amount_mismatch ? (
                  <EmojiIcon describedby='rooster emoji'>🐓</EmojiIcon>
                ) : (
                  <ErrorOutlineIcon />
                )}
              </HighlightIcon>
            )}
          </>
        );

      case 'slip':
        if (request.currency === 'KRW') {
          return (
            <ViewSlip
              disabled={request.has_slip === 0}
              entityId={request.order_id}
              entityType='request'
            />
          );
        }
        return null;

      default:
        return request[key as keyof TRequest];
    }
  };

  const getTbodyBefore = (request: TRequest) => {
    if (userRole !== 'admin' && userRole !== 'operator') return null;
    const isAllowedStatusBulkAction = allowedStatusesBulkAction.includes(
      request.status.toLowerCase(),
    );

    return (
      <td>
        <TableCheckOneRow id={request.id} isDisabled={!isAllowedStatusBulkAction} />
      </td>
    );
  };
  const getTbodyAfter = (request: TRequest) => {
    const isAllowedStatusSingleAction = allowedStatusesSingleAction.includes(
      request.status.toLowerCase(),
    );

    const getApproveEnabled = () => {
      if (request.currency === 'KRW') {
        return request.status !== 'approved' && request.status !== 'rejected';
      }
      return isAllowedStatusSingleAction;
    };

    const getApproveElement = () => {
      if (request.currency === 'KRW') {
        return (
          <ApproveOrderKRW
            entityType='request'
            entityId={request.order_id}
            actualAmount={request.actual_amount}
            requestedAmount={request.requested_amount}
            createdAt={request.created_at}
            refetch={refetch}
          />
        );
      }
      return <ApproveRequest request={request} refetch={refetch} />;
    };

    const actionBtnsConfig: ITableActionsConfig = {
      reject: {
        isEnabled: isAllowedStatusSingleAction,
        isVisible: true,
        popupElem: (
          <ConfirmApproveRejectRequest
            data={{ id: request.id, reason: '' }}
            message='Do you want to reject this request?'
            mutation={useRejectRequestMutation}
            mutationErrorName='request'
            refetch={refetch}
          />
        ),
        popupSize: 'sm',
        allowedRoles: ['admin', 'operator'],
      },
      refund: {
        isEnabled: request.status === 'approved',
        isVisible: type === 'withdrawal',
        popupElem: (
          <ConfirmApproveRefundRequest
            data={{ id: request.id, reason: '' }}
            message='Do you want to refund this request?'
            mutation={useRefundRequestMutation}
            mutationErrorName='request'
            refetch={refetch}
          />
        ),
        popupSize: 'sm',
        allowedRoles: ['admin'],
      },
      view: {
        isEnabled: true,
        isVisible: true,
        popupElem: <ViewRequest id={request.id} type={type} />,
        popupSize: 'lg',
        allowedRoles: ['admin', 'operator'],
      },
      approve: {
        isEnabled: getApproveEnabled(),
        isVisible: true,
        popupElem: getApproveElement(),
        popupSize: 'sm',
      },
    };

    return (
      <td className='action-btns-row'>
        <TableActionBtns config={actionBtnsConfig} />
      </td>
    );
  };

  return {
    columnTitles: getColumnTitles(type, userRole),
    hiddenColumnKeys,

    renderFilterElement,
    getTheadBefore,
    getTheadAfter,

    renderRow,
    getTbodyBefore,
    getTbodyAfter,
  };
};
